import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Edit,
  ArrowLeft,
  Lock,
  Eye,
  EyeOff,
  CreditCard,
  MapPin,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Card,
  CardContent,
  Button,
  Input,
  Label,
  Switch,
  Loader,
} from "@b2b/ui-components";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@b2b/ui-components";
import { ProfileData, UserRole, ProfileField } from "../types/profile";
import { ProfileFieldRenderer } from "./ProfileFieldRender";
import { BrandingMediaSection } from "./BrandingMediaSection";
import {
  BusinessInformationSection,
  type BusinessSubSection,
  getFoundedYearFutureError,
} from "./BusinessInformationSection";
import { TradeInformationSection, type TradeSubSection } from "./TradeInformationSection";
import { FactoryWarehouseSection } from "./FactoryWarehouseSection";
import {
  mapProfileDataToApiUpdate,
  mapApiResponseToProfileData,
} from "../utils/profileMapper";
import { profileService, ProfileServiceResponse } from "@b2b/services";
import type {
  SellerAddressResponse,
  SellerAddressItemRequest,
} from "@b2b/services";
import {
  getSectionsForRole,
  getDefaultSectionId,
  BUYER_PEPAGORA_GROUPS,
  type ProfileSectionDef,
} from "../config/profileSectionConfig";
import {
  getFirstContactInformationViolation,
  findContactRuleForFieldKey,
  focusContactInformationField,
} from "../utils/contactInformationValidation";

/** Sidebar group definitions for Pepagora-style layout */
const PEPAGORA_GROUPS = [
  { label: "Business Information", matchGroupLabel: "Business Information" },
  { label: "Branding & Media", matchId: "branding-media" },
  { label: "Trade Information", matchGroupLabel: "Trade Information" },
  { label: "Factory & Warehouse", matchCustomBlock: "factoryExtended" },
  { label: "Account", matchSellerBlock: true },
] as const;

interface UnifiedProfilePageProps {
  userRole?: UserRole;
  onProfileUpdate?: (updatedData: ProfileData) => void;
  userId?: string; // Optional userId to view another user's profile (admin only)
  initialEditMode?: boolean; // Start in edit mode if true
  /** For sellers: 0-100. Shown as profile completion. At 80%+ can access dashboard, analytics, chat. */
  profileCompletionPercentage?: number;
  /**
   * When true (ProfileSettingsPage only), profiles with role buyer show account email as the read-only
   * “Business Email” control matching seller contact information styling. Omits on other entry points (e.g. /buyer/profile).
   */
  buyerBusinessEmailSettingsField?: boolean;
}

export const UnifiedProfilePage = ({
  userRole,
  onProfileUpdate,
  userId,
  initialEditMode = false,
  profileCompletionPercentage,
  buyerBusinessEmailSettingsField = false,
}: UnifiedProfilePageProps) => {
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [formData, setFormData] = useState<ProfileData | null>(null);
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [detectedRole, setDetectedRole] = useState<UserRole>(
    userRole || "buyer",
  );
  /** Which group's sections are shown in content area */
  const [activeGroupLabel, setActiveGroupLabel] = useState<string>("Business Information");
  /** Which section accordion is currently in edit mode (null = all view-only) */
  const [editingSectionId, setEditingSectionId] = useState<string | null>(
    initialEditMode ? "business-details" : null,
  );
  /** Which accordion is expanded */
  const [expandedSectionId, setExpandedSectionId] = useState<string | null>("business-details");

  // Seller-only blocks state (password, addresses)
  const [sellerAddresses, setSellerAddresses] = useState<
    SellerAddressItemRequest[]
  >([]);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<{
    newPassword?: string;
  }>({});
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockMessage, setBlockMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  /** Snapshot of formData when entering edit mode, for dirty check before discard */
  const formDataSnapshotRef = useRef<string | null>(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [initialValue, setInitialValue] = useState("");

  const navigate = useNavigate();

  // Helper function to ensure email is disabled and all other fields are enabled when editing another user
  const ensureEmailDisabled = (data: ProfileData): ProfileData => {
    if (userId) {
      // Disable email field
      if (data.userInfo?.email) {
        data.userInfo.email.editable = false;
        data.userInfo.email.type = "readonly";
      }

      // Enable all other user-editable fields
      // User Info fields
      if (data.userInfo) {
        // Get the current role to check if user is SUPER_ADMIN
        const roleField = data.userInfo.role;
        const currentRole =
          roleField && typeof roleField === "object" && "value" in roleField
            ? String(roleField.value || "").toUpperCase()
            : "";

        Object.keys(data.userInfo).forEach((key) => {
          const field = data.userInfo[key];
          // Skip system fields that should remain readonly (except isActive and role which should be editable)
          if (
            ![
              "email",
              "userId",
              "sellerStatus",
              "createdAt",
              "updatedAt",
              "auth0Id",
            ].includes(key)
          ) {
            if (field && typeof field === "object" && "editable" in field) {
              field.editable = true;
              // Ensure type is not readonly for editable fields
              if (field.type === "readonly" && key !== "email") {
                // Set isActive to switch type, but disable if user is SUPER_ADMIN
                if (key === "isActive") {
                  if (currentRole === "SUPER_ADMIN") {
                    // Keep account status as readonly for super admins - cannot be changed
                    field.editable = false;
                    field.type = "readonly";
                  } else {
                    field.type = "switch";
                    // Convert value to boolean if it's a string
                    if (typeof field.value === "string") {
                      field.value =
                        field.value === "Active" || field.value === "true";
                    }
                  }
                } else if (key === "role") {
                  // Set role to select type, but disable if user is SUPER_ADMIN
                  const currentRole = String(field.value || "").toUpperCase();
                  if (currentRole === "SUPER_ADMIN") {
                    // Keep role as readonly for super admins - cannot be changed
                    field.editable = false;
                    field.type = "readonly";
                  } else {
                    field.type = "select";
                  }
                } else {
                  field.type = "text";
                }
              }
            }
          }
        });
      }

      // Enable all company info fields
      if (data.companyInfo) {
        Object.keys(data.companyInfo).forEach((key) => {
          const field = data.companyInfo[key];
          if (key === "legalOwnerName" && field && typeof field === "object" && "editable" in field) {
            field.editable = false;
            field.type = "text";
            return;
          }
          if (field && typeof field === "object" && "editable" in field) {
            field.editable = true;
            if (field.type === "readonly") {
              // Determine appropriate type based on field name
              if (
                key.includes("Email") ||
                key.includes("Website") ||
                key.includes("Url")
              ) {
                field.type = "text";
              } else if (
                key.includes("Count") ||
                key.includes("Year") ||
                key.includes("Size")
              ) {
                field.type = "number";
              } else {
                field.type = "text";
              }
            }
          }
        });
      }

      // Enable all social links fields
      if (data.socialLinks) {
        Object.keys(data.socialLinks).forEach((key) => {
          const field = data.socialLinks[key];
          if (field && typeof field === "object" && "editable" in field) {
            field.editable = true;
            if (field.type === "readonly") {
              field.type = "text";
            }
          }
        });
      }
    }
    return data;
  };

  useEffect(() => {
    fetchProfileData();
  }, [userId, initialEditMode]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (editingSectionId !== null) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [editingSectionId]);

  useEffect(() => {
    const nearestPortValue = String(formData?.companyInfo?.nearestPort?.value ?? "");
    if (editingSectionId !== "trade-additional") {
      setAdditionalInfo(nearestPortValue);
      setInitialValue(nearestPortValue);
    }
  }, [formData, editingSectionId]);

  const fetchProfileData = async () => {
    try {
      setIsLoading(true);

      let apiData: ProfileServiceResponse;

      // If userId is provided, fetch that user's profile (admin viewing another user)
      if (userId) {
        const { adminService } = await import("@b2b/services");
        const userData = await adminService.getUserById(userId);

        // Type assertion to access all fields (backend returns full user object)
        const fullUserData = userData as any;
        const fullCompanyData = fullUserData.company as any;

        // Map the user data to ProfileServiceResponse format
        apiData = {
          id: userData.id,
          email: userData.email,
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          phone: userData.phone || "",
          whatsapp: fullUserData.whatsapp || "",
          role: userData.role,
          bio: fullUserData.bio || "",
          website: fullUserData.website || "",
          linkedinProfile: fullUserData.linkedinProfile || "",
          profileImage: fullUserData.profileImage || "",
          businessType: fullUserData.businessType || "",
          country: fullUserData.country || "",
          isActive: userData.isActive,
          sellerStatus: fullUserData.sellerStatus || undefined,
          createdAt: fullUserData.createdAt,
          updatedAt: fullUserData.updatedAt,
          company: fullCompanyData
            ? {
                id: fullCompanyData.id || userData.company.id,
                name: fullCompanyData.name || userData.company.name,
                email: fullCompanyData.email || userData.company.email || "",
                phone: fullCompanyData.phone || "",
                address: fullCompanyData.address || "",
                city: fullCompanyData.city || "",
                state: fullCompanyData.state || "",
                country: fullCompanyData.country || "",
                postalCode: fullCompanyData.postalCode || "",
                website: fullCompanyData.website || "",
                description: fullCompanyData.description || "",
                businessType: fullCompanyData.businessType || "",
                companySize: fullCompanyData.companySize || "",
                employeeCount: fullCompanyData.employeeCount || undefined,
                industry: fullCompanyData.industry || "",
                foundedYear: fullCompanyData.foundedYear || undefined,
                gstNumber: fullCompanyData.gstNumber || "",
                panNumber: fullCompanyData.panNumber || "",
                taxId: fullCompanyData.taxId || "",
                registrationNumber: fullCompanyData.registrationNumber || "",
                businessLicense: fullCompanyData.businessLicense || "",
                factorySize: fullCompanyData.factorySize || "",
                productionCapacity: fullCompanyData.productionCapacity || "",
                qualityCertifications:
                  fullCompanyData.qualityCertifications || "",
                facebookUrl: fullCompanyData.facebookUrl || "",
                linkedinUrl: fullCompanyData.linkedinUrl || "",
                twitterUrl: fullCompanyData.twitterUrl || "",
                logo: fullCompanyData.logo || "",
              }
            : undefined,
          editableFields: [
            "firstName",
            "lastName",
            "phone",
            "whatsapp",
            "bio",
            "website",
            "linkedinProfile",
            "profileImage",
            "businessType",
            "country",
            "companyName",
            "companyEmail",
            "companyPhone",
            "companyAddress",
            "companyCity",
            "companyState",
            "companyCountry",
            "companyPostalCode",
            "companyWebsite",
            "companyDescription",
            "companyBusinessType",
            "companySize",
            "employeeCount",
            "industry",
            "foundedYear",
            "gstNumber",
            "panNumber",
            "taxId",
            "registrationNumber",
            "businessLicense",
            "factorySize",
            "productionCapacity",
            "qualityCertifications",
            "facebookUrl",
            "linkedinUrl",
            "twitterUrl",
            "companyLogo",
          ],
          nonEditableFields: ["email"], // Email is non-editable when admin edits
        } as ProfileServiceResponse;
      } else {
        // Fetch current user's profile (backend may return { success, data } or raw profile)
        const response = await profileService.getProfile();
        const raw = response as unknown as Record<string, unknown>;
        if (raw?.success === true && raw?.data != null) {
          apiData = raw.data as ProfileServiceResponse;
        } else if (
          raw != null &&
          typeof raw.id !== "undefined" &&
          typeof raw.email !== "undefined"
        ) {
          apiData = raw as unknown as ProfileServiceResponse;
        } else {
          throw new Error("Failed to fetch profile");
        }
      }

      // Determine role from API response
      let role: UserRole = "buyer";
      if (apiData.role) {
        const roleStr = apiData.role.toLowerCase();
        if (roleStr === "super_admin" || roleStr === "company_admin") {
          role = "admin";
        } else if (roleStr === "seller" || roleStr === "buyer_seller") {
          role = "seller";
        } else if (roleStr === "buyer") {
          role = "buyer";
        }
      }
      setDetectedRole(role);

      // Map API response to ProfileData
      const mappedData = mapApiResponseToProfileData(apiData);

      // When editing another user (userId provided), disable email field and enable all others
      const processedData = ensureEmailDisabled(mappedData);

      setProfileData(processedData);
      setFormData(processedData);
      // Default to first section of Business Information group
      const defaultSectionId = getDefaultSectionId(
        role,
        !!userId && role !== "buyer",
        role === "seller" ||
          (!!userId &&
            (apiData.role === "SELLER" || apiData.role === "BUYER_SELLER")),
      );
      setExpandedSectionId(defaultSectionId);
      setActiveGroupLabel(
        role === "buyer" ? "Profile" : "Business Information",
      );

      // Seller-only: sync addresses from profile (current user only)
      if (!userId && apiData.company) {
        setSellerAddresses(
          (apiData.company.sellerAddresses ?? []).map(
            (a: SellerAddressResponse) => ({
              id: a.id,
              label: a.label,
              address: a.address,
              city: a.city,
              state: a.state,
              country: a.country,
              postalCode: a.postalCode,
              isDefault: a.isDefault,
            }),
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (
    targetSectionKey?: keyof ProfileData,
    sectionId?: string,
    editingSectionDef?: ProfileSectionDef | null,
  ): {
    isValid: boolean;
    errors: Record<string, Record<string, string>>;
    contactFocusId?: string;
  } => {
    const newErrors: Record<string, Record<string, string>> = {};
    const REQUIRED_FIELD_MESSAGE = "Please enter this field";

    if (!formData)
      return { isValid: false, errors: newErrors, contactFocusId: undefined };
    const companyInfo = formData.companyInfo as Record<string, ProfileField> | undefined;

    const isEmpty = (value: unknown): boolean => {
      if (value == null) return true;
      if (typeof value === "string") return value.trim() === "";
      if (Array.isArray(value)) return value.length === 0;
      return false;
    };

    let contactFocusId: string | undefined;

    const allSectionKeys: (keyof ProfileData)[] = [
      "userInfo",
      "companyInfo",
      "socialLinks",
      "contactPerson",
      "subAccountManagement",
      "products",
    ];
    const sectionKeys: (keyof ProfileData)[] = targetSectionKey
      ? [targetSectionKey]
      : allSectionKeys;

    sectionKeys.forEach((sectionKey) => {
      if (sectionId === "contact-information" && sectionKey === "companyInfo") {
        return;
      }
      const section = formData[sectionKey];
      if (!section || typeof section !== "object" || Array.isArray(section))
        return;
      const sectionObj = section as Record<string, ProfileField>;
      // When saving a single section, only validate that section's fields
      const keysToValidate =
        editingSectionDef &&
        (editingSectionDef.source === sectionKey) &&
        editingSectionDef.fieldKeys?.length
          ? editingSectionDef.fieldKeys
          : null;
      const entries =
        keysToValidate != null
          ? (keysToValidate
              .map((k) => [k, sectionObj[k]] as const)
              .filter(([, f]) => f != null) as [string, ProfileField][])
          : Object.entries(sectionObj);

      entries.forEach(([fieldKey, field]) => {
        if (!field || typeof field !== "object" || !field.editable) return;

        if (
          field.required &&
          (!field.value ||
            (typeof field.value === "string" && field.value.trim() === ""))
        ) {
          if (!newErrors[sectionKey]) newErrors[sectionKey] = {};
          newErrors[sectionKey][fieldKey] =
            sectionId === "contact-information"
              ? REQUIRED_FIELD_MESSAGE
              : `${field.label} is required`;
        }

        if (
          fieldKey === "email" &&
          field.editable &&
          field.value &&
          typeof field.value === "string" &&
          !isValidEmail(field.value)
        ) {
          if (!newErrors[sectionKey]) newErrors[sectionKey] = {};
          newErrors[sectionKey][fieldKey] =
            "Please enter a valid email address";
        }
      });
    });

    if (sectionKeys.includes("companyInfo") && companyInfo && typeof companyInfo === "object") {
      const foundedYearRaw = String(companyInfo.foundedYear?.value ?? "").trim();
      const foundedYearDigits =
        foundedYearRaw === "" || foundedYearRaw === "0"
          ? ""
          : foundedYearRaw.replace(/\D/g, "");
      const fyErr = getFoundedYearFutureError(foundedYearDigits);
      if (fyErr) {
        if (!newErrors.companyInfo) newErrors.companyInfo = {};
        newErrors.companyInfo.foundedYear = fyErr;
      }
    }

    if (sectionId === "contact-information") {
      const firstContact = getFirstContactInformationViolation(companyInfo);
      if (firstContact) {
        newErrors.companyInfo = {
          [firstContact.displayKey]: firstContact.message,
        };
        contactFocusId = firstContact.focusId;
      }
    }

    // Business Details: enforce required Industry selection for custom multi-select.
    if (
      sectionId === "business-details" &&
      companyInfo &&
      typeof companyInfo === "object"
    ) {
      const industryTypesField = companyInfo.industryTypes;
      const selectedIndustryTypes = Array.isArray(industryTypesField?.value)
        ? industryTypesField.value
        : [];

      // Business Details UI stores industries in industryTypes (multi-select chips).
      // Enforce this explicitly so save cannot pass when nothing is selected.
      if (selectedIndustryTypes.length === 0) {
        if (!newErrors.companyInfo) newErrors.companyInfo = {};
        newErrors.companyInfo.industryTypes = "Industry is required";
      }
    }

    // Company Registration Details (only when saving that section): at least one of GST, PAN, or IEC (number or document) required
    if (sectionId === "company-registration" && companyInfo && typeof companyInfo === "object") {
      const gstNumber = String(companyInfo.gstNumber?.value ?? "").trim();
      const panNumber = String(companyInfo.panNumber?.value ?? "").trim();
      const iecNumber = String(companyInfo.iecNumber?.value ?? "").trim();

      const alphaNumericOnlyRegex = /^[a-zA-Z0-9]+$/;

      if (gstNumber && !alphaNumericOnlyRegex.test(gstNumber)) {
        if (!newErrors.companyInfo) newErrors.companyInfo = {};
        newErrors.companyInfo.gstNumber = "GST must contain only letters and numbers";
      }

      if (panNumber && !alphaNumericOnlyRegex.test(panNumber)) {
        if (!newErrors.companyInfo) newErrors.companyInfo = {};
        newErrors.companyInfo.panNumber = "PAN must contain only letters and numbers";
      }

      if (iecNumber && !alphaNumericOnlyRegex.test(iecNumber)) {
        if (!newErrors.companyInfo) newErrors.companyInfo = {};
        newErrors.companyInfo.iecNumber =
          "IEC must contain only letters and numbers";
      }

      const companyRegKeys = ["gstNumber", "gstDocumentUrl", "panNumber", "panDocumentUrl", "iecNumber", "iecDocumentUrl"];
      const hasAny = companyRegKeys.some((key) => {
        const f = companyInfo[key];
        if (!f || f.value == null) return false;
        if (typeof f.value === "string") return f.value.trim().length > 0;
        return true;
      });
      if (!hasAny) {
        if (!newErrors.companyInfo) newErrors.companyInfo = {};
        newErrors.companyInfo.companyRegistrationDetails =
          "Provide at least one: GST, PAN, or IEC (number or document).";
      }
    }

    setErrors(newErrors);
    return {
      isValid: Object.keys(newErrors).length === 0,
      errors: newErrors,
      contactFocusId,
    };
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleFieldChange = (
    sectionKey: string,
    fieldKey: string,
    value: string | File | boolean | string[],
  ) => {
    if (!formData) return;

    setFormData((prev) => ({
      ...prev!,
      [sectionKey]: {
        ...prev![sectionKey as keyof ProfileData],
        [fieldKey]: {
          ...(prev![sectionKey as keyof ProfileData] as Record<string, ProfileField>)[fieldKey],
          value,
        },
      },
    }));

    // Clear contact-information error for this field (single-error mode; includes paired keys)
    const contactRule =
      sectionKey === "companyInfo" ? findContactRuleForFieldKey(fieldKey) : undefined;
    const contactErrKey = contactRule?.displayKey;
    const shouldClearContact =
      sectionKey === "companyInfo" &&
      contactRule &&
      contactErrKey &&
      errors.companyInfo?.[contactErrKey] != null;
    if (shouldClearContact) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors.companyInfo && contactErrKey) {
          const nextCi = { ...newErrors.companyInfo };
          delete nextCi[contactErrKey];
          if (Object.keys(nextCi).length === 0) delete newErrors.companyInfo;
          else newErrors.companyInfo = nextCi;
        }
        return newErrors;
      });
    } else if (errors[sectionKey]?.[fieldKey]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[sectionKey]) {
          delete newErrors[sectionKey][fieldKey];
          if (Object.keys(newErrors[sectionKey]).length === 0) {
            delete newErrors[sectionKey];
          }
        }
        return newErrors;
      });
    }
    // When filling any company-registration field, clear the "at least one required" error
    const companyRegKeys = ["gstNumber", "gstDocumentUrl", "panNumber", "panDocumentUrl", "iecNumber", "iecDocumentUrl"];
    if (sectionKey === "companyInfo" && companyRegKeys.includes(fieldKey) && errors.companyInfo?.companyRegistrationDetails) {
      setErrors((prev) => {
        const next = { ...prev };
        if (next.companyInfo) {
          const nextCi = { ...next.companyInfo };
          delete nextCi.companyRegistrationDetails;
          next.companyInfo = nextCi;
        }
        return next;
      });
    }

    // Self-service: refresh header avatar when buyer profile photo or seller company logo updates
    if (!userId && typeof value === "string") {
      const buyerProfileChanged =
        detectedRole === "buyer" &&
        sectionKey === "userInfo" &&
        fieldKey === "profilePic";
      const sellerLogoChanged =
        detectedRole === "seller" &&
        sectionKey === "companyInfo" &&
        fieldKey === "companyLogo";

      if (buyerProfileChanged || sellerLogoChanged) {
        window.dispatchEvent(
          new CustomEvent("bt_b2b_profile_updated", {
            detail: { profileImageUrl: value },
          }),
        );
      }
    }
  };

  const handleExtendedChange = (businessProfileExtended: ProfileData["businessProfileExtended"]) => {
    if (!formData || !businessProfileExtended) return;
    setFormData((prev) => ({
      ...prev!,
      businessProfileExtended,
    }));
  };

  const handleSave = async (
    targetSectionKey?: keyof ProfileData,
    sectionId?: string,
    editingSectionDef?: ProfileSectionDef | null,
  ): Promise<boolean> => {
    if (!formData) return false;

    const validationResult = validateForm(targetSectionKey, sectionId, editingSectionDef);
    if (!validationResult.isValid) {
      if (validationResult.contactFocusId) {
        focusContactInformationField(validationResult.contactFocusId);
      }
      return false;
    }

    try {
      setIsSaving(true);
      const updateData = mapProfileDataToApiUpdate(formData);

      if (Object.keys(updateData).length === 0) {
        alert("No changes to save.");
        return false;
      }

      let response;

      // If userId is provided, use admin service to update that user
      // Otherwise, use profile service to update current user
      if (userId) {
        const { adminService } = await import("@b2b/services");

        // Admin service now handles both user and company fields
        response = await adminService.updateUser(userId, updateData);

        if (response.success || response.data) {
          // Refetch profile data
          await fetchProfileData();

          if (onProfileUpdate && profileData) {
            onProfileUpdate(profileData);
          }

          showBlockMessage("success", "Profile updated.");
        } else {
          throw new Error(response.message || "Failed to update profile");
        }
      } else {
        response = await profileService.updateProfile(updateData);

        if (response.success && response.data) {
          // Refetch profile data
          await fetchProfileData();

          if (onProfileUpdate && profileData) {
            onProfileUpdate(profileData);
          }

          showBlockMessage("success", "Profile updated.");
          window.dispatchEvent(new CustomEvent("bt_b2b_profile_updated"));
        } else {
          throw new Error(response.message || "Failed to update profile");
        }
      }
    } catch (error: any) {
      console.error("Error updating profile:", error);
      alert(error.message || "Failed to update profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
    return true;
  };

  const handleCancel = () => {
    if (profileData) {
      const resetData = ensureEmailDisabled({ ...profileData });
      setFormData(resetData);
    }
    setErrors({});
  };

  const closeEditMode = () => {
    setEditingSectionId(null);
    formDataSnapshotRef.current = null;
    setShowDiscardConfirm(false);
  };

  const handleDiscard = () => {
    const isDirty =
      formDataSnapshotRef.current != null &&
      formData &&
      JSON.stringify(formData) !== formDataSnapshotRef.current;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    handleCancel();
    closeEditMode();
  };

  const handleDiscardConfirm = () => {
    handleCancel();
    closeEditMode();
  };

  const showBlockMessage = (type: "success" | "error", text: string) => {
    setBlockMessage({ type, text });
    setTimeout(() => setBlockMessage(null), 5000);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordErrors({
        newPassword: "This password is already used. Please try a new password",
      });
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showBlockMessage(
        "error",
        "New password and confirm password do not match",
      );
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      showBlockMessage("error", "Password must be at least 8 characters");
      return;
    }
    try {
      setPasswordSaving(true);
      setBlockMessage(null);
      const { authService } = await import("@b2b/services");
      await authService.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });
      setPasswordErrors({});
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      showBlockMessage("success", "Password changed successfully.");
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      showBlockMessage(
        "error",
        e?.response?.data?.message || e?.message || "Failed to change password",
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  /** Upload a file to DigitalOcean Spaces and return the public URL */
  const uploadFile = async (file: File): Promise<string> => {
    return profileService.uploadFile(file);
  };

  const handleSaveAddresses = async () => {
    try {
      setBlockSaving(true);
      setBlockMessage(null);
      const list: SellerAddressItemRequest[] = sellerAddresses.map((a) => ({
        ...(a.id ? { id: a.id } : {}),
        label: a.label,
        address: a.address,
        city: a.city,
        state: a.state,
        country: a.country,
        postalCode: a.postalCode,
        isDefault: a.isDefault,
      }));
      await profileService.updateProfile({ sellerAddresses: list });
      await fetchProfileData();
      showBlockMessage("success", "Addresses saved.");
    } catch (err: unknown) {
      const e = err as { message?: string };
      showBlockMessage("error", e?.message || "Failed to save addresses");
    } finally {
      setBlockSaving(false);
    }
  };

  const roleColors: Record<UserRole, string> = {
    buyer: "bg-blue-500/10 text-blue-700 border-blue-200",
    seller: "bg-green-500/10 text-green-700 border-green-200",
    admin: "bg-purple-500/10 text-purple-700 border-purple-200",
  };

  // Compute sections and nav state before any early return so hooks run in same order every time
  const useBusinessProfileLayout =
    detectedRole === "seller" ||
    (!!userId &&
      (formData?.userInfo?.role?.value === "SELLER" ||
        formData?.userInfo?.role?.value === "BUYER_SELLER"));
  const isViewingOtherUserForSections = !!userId && detectedRole !== "buyer";
  const sections = getSectionsForRole(
    detectedRole,
    isViewingOtherUserForSections,
    useBusinessProfileLayout,
  );

  const sidebarGroups =
    detectedRole === "buyer" ? BUYER_PEPAGORA_GROUPS : PEPAGORA_GROUPS;

  const getSectionsForGroup = (groupLabel: string): ProfileSectionDef[] => {
    const group = sidebarGroups.find((g) => g.label === groupLabel);
    if (!group) return [];
    return sections.filter((s) => {
      if ("matchGroupLabel" in group) return s.groupLabel === group.matchGroupLabel;
      if ("matchId" in group) return s.id === group.matchId;
      if ("matchCustomBlock" in group) return s.customBlock === group.matchCustomBlock;
      if ("matchSellerBlock" in group) return !!s.sellerBlock;
      return false;
    });
  };

  const sectionsForActiveGroup = getSectionsForGroup(activeGroupLabel);
  const editingSection =
    editingSectionId
      ? sectionsForActiveGroup.find(
          (s) => s.id === editingSectionId && !s.sellerBlock
        )
      : null;
  const showEditingBar = !!editingSection;
  const isTradeAdditionalSection = editingSection?.id === "trade-additional";
  const isChanged = additionalInfo.trim() !== initialValue.trim();

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-4 bg-gray-50">
        <Loader size={40} color="#f97316" />
        <p className="text-sm text-gray-600">Loading your profile…</p>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3 bg-gray-50">
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-8 text-center shadow-sm max-w-md">
          <p className="text-gray-600">No profile data available</p>
        </div>
      </div>
    );
  }

  const firstName =
    (typeof formData.userInfo?.firstName?.value === "string"
      ? formData.userInfo.firstName.value
      : "") || "";
  const lastName =
    (typeof formData.userInfo?.lastName?.value === "string"
      ? formData.userInfo.lastName.value
      : "") || "";
  const emailValue =
    typeof formData.userInfo?.email?.value === "string"
      ? formData.userInfo.email.value
      : "";
  const namePart = `${firstName} ${lastName}`.trim();
  const fullName = namePart || emailValue || "User";
  const profileImage =
    (typeof formData.userInfo?.profilePic?.value === "string"
      ? formData.userInfo.profilePic.value
      : "") || "";
  const email = emailValue || "";
  const roleValue = formData.userInfo?.role?.value || detectedRole;
  const sellerStatus = formData.userInfo?.sellerStatus?.value;

  const getFieldsForSection = (
    def: ProfileSectionDef,
  ): Record<string, ProfileField> => {
    if (!def.source || !def.fieldKeys?.length) return {};
    const sourceData = formData[def.source];
    if (!sourceData) return {};
    const out: Record<string, ProfileField> = {};
    def.fieldKeys.forEach((key) => {
      if (sourceData[key]) out[key] = sourceData[key];
    });
    return out;
  };

  const getSectionKey = (def: ProfileSectionDef): keyof ProfileData => {
    if (def.source === "userInfo") return "userInfo";
    if (def.source === "companyInfo") return "companyInfo";
    return "socialLinks";
  };

  /** Renders the form content for a given section definition */
  const renderSectionContent = (sectionDef: ProfileSectionDef, isEditing: boolean): React.ReactNode => {
    if (!formData) return null;
    const sectionKey = getSectionKey(sectionDef);
    const errs = errors[sectionKey] ?? {};

    if (sectionDef.groupLabel === "Business Information") {
      return (
        <BusinessInformationSection
          subSection={sectionDef.id as BusinessSubSection}
          formData={formData}
          isEditing={isEditing}
          onFieldChange={(fieldKey, value) => handleFieldChange(sectionKey, fieldKey, value)}
          errors={errs}
          onUpload={uploadFile}
        />
      );
    }
    if (sectionDef.customBlock === "brandingExtended") {
      return (
        <BrandingMediaSection
          formData={formData}
          isEditing={isEditing}
          onFieldChange={(fieldKey, value) => handleFieldChange(sectionKey, fieldKey, value)}
          onExtendedChange={handleExtendedChange}
          errors={errs}
          onUpload={uploadFile}
        />
      );
    }
    if (sectionDef.groupLabel === "Trade Information") {
      return (
        <TradeInformationSection
          subSection={sectionDef.id as TradeSubSection}
          formData={formData}
          isEditing={isEditing}
          onFieldChange={(fieldKey, value) => {
            handleFieldChange(sectionKey, fieldKey, value);
            if (
              sectionDef.id === "trade-additional" &&
              fieldKey === "nearestPort" &&
              typeof value === "string"
            ) {
              setAdditionalInfo(value);
            }
          }}
          errors={errs}
        />
      );
    }
    if (sectionDef.customBlock === "factoryExtended") {
      return (
        <FactoryWarehouseSection
          formData={formData}
          isEditing={isEditing}
          onFieldChange={(fieldKey, value) => handleFieldChange(sectionKey, fieldKey, value)}
          errors={errs}
          onUpload={uploadFile}
        />
      );
    }
    // Generic field renderer fallback
    const fields = getFieldsForSection(sectionDef);
    if (Object.keys(fields).length === 0) return null;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        {Object.entries(fields).map(([key, field]) => {
          if (
            buyerBusinessEmailSettingsField &&
            detectedRole === "buyer" &&
            key === "email"
          ) {
            const display =
              typeof field.value === "string"
                ? field.value
                : String(field.value ?? "");
            const emailErr = errs[key];
            return (
              <div key={key} className="space-y-1.5">
                <span className="text-sm font-medium text-gray-700 block">
                   Email{" "}
                  {isEditing && <span className="text-red-600">*</span>}
                </span>
                <div
                  id="buyer-profile-business-email"
                  tabIndex={isEditing ? 0 : -1}
                  role="group"
                  aria-required={!!field.required}
                  aria-invalid={!!emailErr}
                  className={`h-10 px-3 py-2 border rounded bg-gray-50 text-gray-700 text-sm flex items-center outline-none focus:ring-0 focus-visible:ring-0 ${
                    emailErr ? "border-destructive" : "border-gray-300"
                  }`}
                >
                  {display.trim() ? display : "—"}
                </div>
                {emailErr && (
                  <p className="text-sm text-destructive">{emailErr}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Read-only here. Add or update your business email in account
                  settings if missing.
                </p>
              </div>
            );
          }
          return (
            <ProfileFieldRenderer
              key={key}
              field={field}
              fieldKey={key}
              isEditing={isEditing}
              onValueChange={(fieldKey, value) =>
                handleFieldChange(sectionKey, fieldKey, value)
              }
              error={errs[key]}
              onUpload={uploadFile}
              maxFileSizeBytes={
                key === "profilePic" ? 2 * 1024 * 1024 : undefined
              }
              padBuyerProfileCountrySearchField={detectedRole === "buyer"}
              hideRequiredAsteriskUnlessEditing={
                detectedRole === "buyer" && buyerBusinessEmailSettingsField
              }
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full min-w-0 bg-gray-50 min-h-screen flex">
      {/* ─── Pepagora Sidebar ─── */}
      <aside className="w-56 flex-shrink-0 hidden lg:flex flex-col bg-white border-r border-gray-200 min-h-screen sticky top-0 h-screen overflow-y-auto">
        {/* Back link */}
        <div className="px-4 py-4 border-b border-gray-100">
          <button
            type="button"
            onClick={() => {
              if (userRole === "admin") {
                navigate(-1);
                return;
              }
              if (detectedRole === "seller") navigate("/seller/dashboard");
              else navigate("/buyer/dashboard");
            }}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            aria-label={userRole === "admin" ? "Go back" : "Business profile"}
          >
            <ArrowLeft className="h-4 w-4" />
            {/* {detectedRole === "buyer" ? "Profile settings" : "Business Profile"} */}
            {userRole === "admin" ? "Back" : "Business Profile"}
          </button>
        </div>

        {/* Group navigation */}
        <nav className="flex-1 py-2">
          {sidebarGroups.map((group) => {
            const groupSections = getSectionsForGroup(group.label);
            if (groupSections.length === 0) return null;
            const isActiveGroup = activeGroupLabel === group.label;

            return (
              <div key={group.label}>
                <button
                  type="button"
                  onClick={() => {
                    setActiveGroupLabel(group.label);
                    setExpandedSectionId(groupSections[0]?.id ?? null);
                    setEditingSectionId(null);
                  }}
                  className={`w-full text-left flex items-center px-4 py-3 text-sm transition-colors ${
                    isActiveGroup
                      ? "border-l-2 border-orange-500 text-orange-600 font-medium bg-orange-50/40"
                      : "border-l-2 border-transparent text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  {group.label}
                </button>

                {/* Sub-items when active group and multiple sections */}
                {isActiveGroup && groupSections.length > 1 && (
                  <div className="pl-5 pb-1">
                    {groupSections.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          setExpandedSectionId(s.id);
                          setEditingSectionId(null);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-sm rounded transition-colors ${
                          expandedSectionId === s.id
                            ? "text-orange-600 font-medium"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        · {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* ─── Main content ─── */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile: group selector */}
        <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-200">
          <select
            value={activeGroupLabel}
            onChange={(e) => {
              const grp = e.target.value;
              setActiveGroupLabel(grp);
              const first = getSectionsForGroup(grp)[0];
              setExpandedSectionId(first?.id ?? null);
              setEditingSectionId(null);
            }}
            className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
            aria-label="Select section group"
          >
            {sidebarGroups.map((g) => {
              const grpSections = getSectionsForGroup(g.label);
              if (grpSections.length === 0) return null;
              return <option key={g.label} value={g.label}>{g.label}</option>;
            })}
          </select>
        </div>

        {/* Block message */}
        {blockMessage && (
          <div className="px-6 pt-4">
            <div
              role="alert"
              className={`p-4 rounded-lg border flex items-center gap-3 ${
                blockMessage.type === "success"
                  ? "bg-green-50 text-green-800 border-green-200"
                  : "bg-red-50 text-red-800 border-red-200"
              }`}
            >
              {blockMessage.type === "success" ? (
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
              )}
              {blockMessage.text}
            </div>
          </div>
        )}

        {/* Accordion content area */}
        <main
          className={`px-6 py-6 space-y-4 ${showEditingBar ? "pb-24" : ""}`}
        >
          {sectionsForActiveGroup.map((sectionDef) => {
            const isExpanded = expandedSectionId === sectionDef.id;
            const isEditing = editingSectionId === sectionDef.id;
            const isSellerBlock = !!sectionDef.sellerBlock;
            const isBuyerOwnProfile = detectedRole === "buyer";
            const isSingleSubsectionGroup =
              isBuyerOwnProfile && sectionsForActiveGroup.length === 1;

            return (
              <div
                key={sectionDef.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              >
                {/* Accordion header */}
                <div
                  className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    if (isSingleSubsectionGroup) return;
                    setExpandedSectionId((prev) =>
                      prev === sectionDef.id ? null : sectionDef.id,
                    );
                    if (editingSectionId === sectionDef.id) setEditingSectionId(null);
                  }}
                >
                  <div className="flex flex-col gap-0.5">
                    <h3 className="text-base font-semibold text-gray-800">
                      {sectionDef.label}
                    </h3>
                    {isExpanded && !isEditing && !isSellerBlock && (
                      <p className="text-xs text-muted-foreground font-normal">
                        Click Edit to update this section.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {isExpanded && !isEditing && !isSellerBlock && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          try {
                            formDataSnapshotRef.current = formData
                              ? JSON.stringify(formData)
                              : null;
                          } catch {
                            formDataSnapshotRef.current = null;
                          }
                          setEditingSectionId(sectionDef.id);
                        }}
                        className="text-sm h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        <Edit className="h-3.5 w-3.5 mr-1.5" />
                        Edit section
                      </Button>
                    )}
                    {!isSingleSubsectionGroup &&
                      (isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ))}
                  </div>
                </div>

                {/* Accordion body */}
                {isExpanded && (
                  <div className="px-6 py-5 border-t border-gray-100">
                    {/* Seller block: Password */}
                    {sectionDef.sellerBlock === "password" && (
                      <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Current password</Label>
                          <div className="relative mt-1.5">
                            <Input
                              type={passwordVisibility.currentPassword ? "text" : "password"}
                              value={passwordForm.currentPassword}
                              onChange={(e) => {
                                setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }));
                                if (passwordErrors.newPassword) {
                                  setPasswordErrors((prev) => ({ ...prev, newPassword: undefined }));
                                }
                              }}
                              placeholder="Current password"
                              className="h-10 border-gray-300 rounded pr-10"
                              required
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setPasswordVisibility((prev) => ({
                                  ...prev,
                                  currentPassword: !prev.currentPassword,
                                }))
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              aria-label={
                                passwordVisibility.currentPassword
                                  ? "Hide current password"
                                  : "Show current password"
                              }
                            >
                              {passwordVisibility.currentPassword ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-gray-700">New password</Label>
                          <div className="relative mt-1.5">
                            <Input
                              type={passwordVisibility.newPassword ? "text" : "password"}
                              value={passwordForm.newPassword}
                              onChange={(e) => {
                                setPasswordForm((p) => ({ ...p, newPassword: e.target.value }));
                                if (passwordErrors.newPassword) {
                                  setPasswordErrors((prev) => ({ ...prev, newPassword: undefined }));
                                }
                              }}
                              placeholder="New password"
                              className="h-10 border-gray-300 rounded pr-10"
                              required
                              minLength={8}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setPasswordVisibility((prev) => ({
                                  ...prev,
                                  newPassword: !prev.newPassword,
                                }))
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              aria-label={
                                passwordVisibility.newPassword
                                  ? "Hide new password"
                                  : "Show new password"
                              }
                            >
                              {passwordVisibility.newPassword ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                          {passwordErrors.newPassword && (
                            <p className="mt-1.5 text-sm text-red-600">{passwordErrors.newPassword}</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Confirm password</Label>
                          <div className="relative mt-1.5">
                            <Input
                              type={passwordVisibility.confirmPassword ? "text" : "password"}
                              value={passwordForm.confirmPassword}
                              onChange={(e) => setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                              placeholder="Confirm new password"
                              className="h-10 border-gray-300 rounded pr-10"
                              required
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setPasswordVisibility((prev) => ({
                                  ...prev,
                                  confirmPassword: !prev.confirmPassword,
                                }))
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              aria-label={
                                passwordVisibility.confirmPassword
                                  ? "Hide confirm password"
                                  : "Show confirm password"
                              }
                            >
                              {passwordVisibility.confirmPassword ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <Button type="submit" disabled={passwordSaving} className="bg-orange-500 hover:bg-orange-600 text-white">
                          {passwordSaving ? "Changing..." : "Change password"}
                        </Button>
                      </form>
                    )}

                    {/* Regular section content */}
                    {!isSellerBlock && renderSectionContent(sectionDef, isEditing)}
                  </div>
                )}
              </div>
            );
          })}
        </main>

        {/* Fixed bar at viewport bottom when editing - responsive, no sidebar overlap */}
        {showEditingBar && editingSection && (
            <div
              className="fixed z-50 bottom-0 left-0 right-0 lg:left-[30rem] flex items-center justify-between gap-4 px-4 sm:px-6 py-4 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
              role="region"
              aria-label="Editing section actions"
            >
              <p className="text-sm font-medium text-gray-700">
                Editing <span className="text-orange-600">{editingSection.label}</span>
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleDiscard}
                  disabled={isSaving || (isTradeAdditionalSection && !isChanged)}
                  className={`border border-gray-300 text-gray-600 ${
                    isTradeAdditionalSection && !isChanged ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Discard changes
                </Button>
                <Button
                  onClick={async () => {
                    const sectionKey = editingSection ? getSectionKey(editingSection) : undefined;
                    const ok = await handleSave(sectionKey, editingSection?.id, editingSection ?? undefined);
                    if (ok) {
                      closeEditMode();
                    }
                  }}
                  disabled={isSaving || (isTradeAdditionalSection && !isChanged)}
                  className={`bg-orange-500 hover:bg-orange-600 text-white ${
                    isTradeAdditionalSection && !isChanged ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSaving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  {isSaving ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </div>
        )}
      </div>

      {/* Unsaved changes confirmation */}
      <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Discard them? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDiscardConfirm(false)}>
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDiscardConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
